// CODE COPIED FROM STACKOVERFLOW

/**
 * Generates a sequential rank string (increments the last rank).
 * Mirrors the backend PHP logic from Text::getNextRank()
 *
 * Use this when adding an element at the end of a list.
 *
 * Examples:
 * - generateNextRankString("") -> "d"
 * - generateNextRankString("d") -> "e"
 * - generateNextRankString("z") -> "zd"
 * - generateNextRankString("aa") -> "ab"
 * - generateNextRankString("az") -> "ba"
 * - generateNextRankString("zz") -> "zzd"
 *
 * @param {string} lastRank - The rank of the last element
 * @returns {string} The next rank string in sequence
 */
export const generateNextRankString = (lastRank) => {
  if (!lastRank) {
    return "d";
  }

  // If ends with 'z', append 'd'
  if (lastRank.endsWith("z")) {
    return lastRank + "d";
  }

  // Increment string (mimicking PHP's $string++ behavior)
  const chars = lastRank.split("").map((c) => c.charCodeAt(0));

  // Increment from the last character
  for (let i = chars.length - 1; i >= 0; i--) {
    if (chars[i] < 122) {
      // if not 'z'
      chars[i]++;
      return String.fromCharCode(...chars);
    }
    // If it's 'z', set to 'a' and continue to increment the previous character
    chars[i] = 97; // reset to 'a'
  }

  // If we got here, all characters were 'z', so prepend with 'a'
  // (but this case is already handled by the endsWith('z') check above)
  return "a" + lastRank;
};

export const generateRankString = (prev, next, useReversedOrder = false) => {
  // if we are generating rank for a list which is ordered DESC, a rank that
  // is previous in terms of items order is actually next in terms of alphanum
  // sort and vice versa. So we need to flip these variable for such case.

  // Variables flip:
  if (useReversedOrder) {
    [prev, next] = [next, prev];
  }

  // Special case: if next is empty, we're adding a new element at the end
  // Use sequential generation instead of calculating the middle point
  if (next === "") {
    return generateNextRankString(prev);
  }

  let p;
  let n;
  let pos;
  let str;
  for (pos = 0; p == n; pos++) {
    // find leftmost non-matching character
    p = pos < prev.length ? prev.charCodeAt(pos) : 96;
    n = pos < next.length ? next.charCodeAt(pos) : 123;
  }
  str = prev.slice(0, pos - 1); // copy identical part of string
  if (p == 96) {
    // prev string equals beginning of next
    while (n == 97) {
      // next character is 'a'
      n = pos < next.length ? next.charCodeAt(pos++) : 123; // get char from next
      str += "a"; // insert an 'a' to match the 'a'
    }
    if (n == 98) {
      // next character is 'b'
      str += "a"; // insert an 'a' to match the 'b'
      n = 123; // set to end of alphabet
    }
  } else if (p + 1 == n) {
    // found consecutive characters
    str += String.fromCharCode(p); // insert character from prev
    n = 123; // set to end of alphabet
    while ((p = pos < prev.length ? prev.charCodeAt(pos++) : 96) == 122) {
      // p='z'
      str += "z"; // insert 'z' to match 'z'
    }
  }
  return str + String.fromCharCode(Math.ceil((p + n) / 2)); // append middle character
};

export const DEFAULT_RANK = "aa";
